import styled from "styled-components";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Table as UITable,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";
import { formatToBRL } from "@/utils/formatToMoney";
import { getPagesToShow } from "@/utils/pagination";
import type { Meta } from "src/@types/paginated";

import type { IDepositRow } from "src/@types/deposit-reconciliation";
import {
  DEPOSIT_TABLE_COLUMN_COUNT,
  DEPOSIT_TABLE_HEADERS,
  TABLE_EMPTY_TEXT,
} from "../constants";
import {
  depositVariantOf,
  isDepositSelectable,
  itemRowClass,
} from "../utils/item-status";
import type { DepositFilterKey, DepositFilterValues, OptionType } from "../types";
import DepositFilters from "./deposit-filters";
import ItemsTableBody from "./items-table-body";
import { ItemStatusBadge } from "./item-status-badge";
import StatementDescriptionCell from "./statement-description-cell";
import PageSizeSelect from "../../components/page-size-select";

interface DepositTableProps {
  data: IDepositRow[];
  page: number;
  pageSize: number;
  total: number;
  isFetching: boolean;
  filters: DepositFilterValues;
  onFilterChange: (key: DepositFilterKey, value: string | undefined) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: (ids: number[], checked: boolean) => void;
  bankOptions?: readonly OptionType[];
  periodStart?: string;
  periodEnd?: string;
}

export default function DepositTable({
  data,
  page,
  pageSize,
  total,
  isFetching,
  filters,
  onFilterChange,
  onPageChange,
  onPageSizeChange,
  selectedIds,
  onToggle,
  onToggleAll,
  bankOptions,
  periodStart,
  periodEnd,
}: DepositTableProps): JSX.Element {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const meta: Meta = {
    currentPage: page,
    lastPage,
    perPage: pageSize,
    total,
    prev: page > 1 ? page - 1 : null,
    next: page < lastPage ? page + 1 : null,
  };

  const selectableIds = data.filter(isDepositSelectable).map(d => d.id);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));

  return (
    <div className="border border-slate-200 rounded-md bg-white">
      <div className="border-b border-slate-200 p-3 bg-slate-50">
        <h3 className="font-semibold text-sm text-slate-700 mb-2">
          Depósitos no extrato bancário
          <span className="ml-2 text-slate-500 font-normal">({total})</span>
        </h3>
        <DepositFilters
          filters={filters}
          onFilterChange={onFilterChange}
          bankOptions={bankOptions}
          periodStart={periodStart}
          periodEnd={periodEnd}
        />
      </div>

      <UITable>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10 text-center">
              <Checkbox
                checked={allSelected}
                onCheckedChange={c =>
                  onToggleAll(selectableIds, Boolean(c))
                }
                disabled={selectableIds.length === 0}
                aria-label="Selecionar todos"
              />
            </TableHead>
            <TableHead className="text-xs text-center">{DEPOSIT_TABLE_HEADERS.BANK}</TableHead>
            <TableHead className="text-xs text-center">{DEPOSIT_TABLE_HEADERS.AGENCY}</TableHead>
            <TableHead className="text-xs text-center">{DEPOSIT_TABLE_HEADERS.ACCOUNT}</TableHead>
            <TableHead className="text-xs text-center">{DEPOSIT_TABLE_HEADERS.DATE}</TableHead>
            <TableHead className="text-xs text-center">{DEPOSIT_TABLE_HEADERS.AMOUNT}</TableHead>
            <TableHead className="text-xs text-center">{DEPOSIT_TABLE_HEADERS.DESCRIPTION}</TableHead>
            <TableHead className="text-xs text-center">{DEPOSIT_TABLE_HEADERS.STATUS}</TableHead>
          </TableRow>
        </TableHeader>
        <ItemsTableBody
          isFetching={isFetching}
          isEmpty={data.length === 0}
          columnCount={DEPOSIT_TABLE_COLUMN_COUNT}
          emptyText={TABLE_EMPTY_TEXT.DEPOSIT}
        >
          {data.map(dep => {
            const selectable = isDepositSelectable(dep);
            const variant = depositVariantOf(dep);
            const rowClass = itemRowClass(
              dep.isInDraft,
              selectable,
              selectedIds.has(dep.id),
            );
            return (
              <TableRow key={dep.id} className={rowClass}>
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(dep.id)}
                    onCheckedChange={() => onToggle(dep.id)}
                    disabled={!selectable}
                    aria-label={`Selecionar depósito ${dep.id}`}
                  />
                </TableCell>
                <TableCell className="text-xs">{dep.bankName}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{dep.agency}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{dep.accountNumber}</TableCell>
                <TableCell className="text-xs">
                  {formatDateToDMY(dep.depositDate)}
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap">
                  {formatToBRL(dep.amount)}
                </TableCell>
                <StatementDescriptionCell description={dep.statementDescription} />
                <TableCell>
                  <ItemStatusBadge
                    variant={variant}
                    batchId={dep.batchId}
                    draftId={dep.draftId}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </ItemsTableBody>
      </UITable>

      <div className="flex w-full items-center justify-between px-3 py-2 text-xs border-t border-slate-200">
        <PaginationButtonsContainer>
          {data.length !== 0 && (
            <>
              <button
                className={`navigate-btn previous ${
                  meta.prev !== null && !isFetching ? "" : "disabled"
                }`}
                onClick={() => meta.prev !== null && onPageChange(meta.prev)}
                disabled={meta.prev === null || isFetching}
              >
                Anterior
              </button>
              {getPagesToShow(meta).map((p, idx) => (
                <span
                  key={`${p}-${idx}`}
                  className={`pages ${p === meta.currentPage ? "selected" : ""} ${
                    p === "..." ? "ocult" : ""
                  }`}
                  onClick={() => typeof p === "number" && onPageChange(p)}
                >
                  {p}
                </span>
              ))}
              <button
                className={`navigate-btn next ${
                  meta.next !== null && !isFetching ? "" : "disabled"
                }`}
                onClick={() => meta.next !== null && onPageChange(meta.next)}
                disabled={meta.next === null || isFetching}
              >
                Próximo
              </button>
            </>
          )}
        </PaginationButtonsContainer>
        <div className="flex items-center gap-4">
          <PageSizeSelect value={pageSize} onChange={onPageSizeChange} />
          <span className="text-slate-600">
            Página {page} de {lastPage}
          </span>
        </div>
      </div>
    </div>
  );
}

const PaginationButtonsContainer = styled.div`
  display: flex;
  font-size: 14px;

  .pages {
    display: flex;
    justify-content: center;
    padding: 8px 6px;
    border: 1px solid #dcdce3;
    border-left: none;
    border-top: none;
    min-width: 29px;

    &.selected {
      background-color: #f4c83e;
    }
  }

  .pages:not(.ocult) {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .navigate-btn {
    cursor: pointer;
    padding: 6px 12px;
    background-color: #2b0c1a;
    color: #fff;

    &.previous {
      border-radius: 0 0 0 8px;
    }

    &.next {
      border-radius: 0 0 8px 0;
    }

    &:hover {
      text-decoration: underline;
    }

    &.disabled {
      color: #8d91a2;
      background-color: #d9dae0;
      cursor: not-allowed;
      text-decoration: none;
    }
  }
`;
